import React from "react"
import CalendarTutorial from "../../components/calendar-tutorial"

const tutorial = {
  title: "Create and Share a School Club Schedule",
  description:
    "Create a clear school club calendar with session details, a read-only link, optional password protection, and updates at the same URL.",
  intro:
    "Give club members and families a clear place to check session times. This walkthrough uses a school robotics club to show how to add sessions, include useful details, publish a calendar, and update it when arrangements change.",
  rows: [
    ["Monday", "3:30 pm", "Robotics: build session", "60 min"],
    ["Wednesday", "3:30 pm", "Robotics: testing session", "60 min"],
    ["Friday", "3:30 pm", "Robotics: team demonstration", "45 min"],
  ],
  prompt:
    "For the week starting Monday [YYYY-MM-DD], create these robotics club events:\nMonday 3:30pm Robotics build session for 1 hour\nWednesday 3:30pm Robotics testing session for 1 hour\nFriday 3:30pm Robotics team demonstration for 45 minutes",
  steps: [
    {
      title: "1. Create a calendar for the club",
      paragraphs: [
        "Choose New blank calendar and open Calendar Settings. Use a title families will recognise, such as “Robotics Club — Weekly Sessions”, and add a short description of what the calendar covers.",
        "Navigate to the week you are planning. Use your confirmed club times in place of the example sessions.",
      ],
    },
    {
      title: "2. Add sessions and check the times",
      paragraphs: [
        "Create the sessions manually, or replace the Monday date in the prompt above and send it to Magic Generate. Review the proposed dates and durations, then choose Add to calendar.",
        "Give each event a useful title, such as “Robotics: testing session”, so members can distinguish it from a general club meeting.",
      ],
    },
    {
      title: "3. Include the details families need",
      paragraphs: [
        "Open each event to review its description and timing. Include the meeting room, what members should bring, and the collection arrangements where relevant. For example: “Meet in Room 12. Bring your project notebook. Collection at 4:30pm.”",
        "Check the end time against your collection instructions. Preview the finished calendar before publishing so that the event details and timetable agree.",
      ],
    },
    {
      title: "4. Publish the club calendar",
      paragraphs: [
        "Select Publish calendar. Review the calendar title, description, and event details. If you want viewers to enter a password, fill in Optional password before selecting Publish.",
        "Copy the published link and open it in another browser or on a phone. For a password-protected calendar, check that the password prompt appears and that the password opens the view.",
      ],
    },
    {
      title: "5. Give members the link",
      paragraphs: [
        "Share the calendar URL through your usual club communication channel. If you set a password, provide it to the intended viewers too.",
        "The published calendar is read-only. Families can view the sessions without changing the schedule you maintain.",
      ],
    },
    {
      title: "6. Republish when a session changes",
      paragraphs: [
        "Suppose Wednesday’s session starts at 4pm instead of 3:30pm. Edit the event and check its end time and collection details. Open Publish calendar and select Republish.",
        "The URL stays the same. Tell families about a time change through your usual channel and ask them to reopen or refresh the calendar. Republishing updates the calendar content; it does not send an automatic notification.",
      ],
    },
  ],
  cta: "Create your school club calendar",
  relatedUrl: "/how-to-guides/move-calendar-events-with-chat/",
  relatedTitle: "Move Your Calendar Events with One Chat Request",
}

export default function TutorialPage() {
  return <CalendarTutorial {...tutorial} />
}
